import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Target, TrendingUp } from 'lucide-react';
import { siteData } from '../data/mock';

const APropos = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20">
      {/* Hero */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animated">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                {siteData.coachBio.name}, ton coach
              </h1>
              <p className="text-2xl text-gray-300 mb-6">
                {siteData.coachBio.title}
              </p>
              <p className="text-xl text-gray-300">
                {siteData.coachBio.description}
              </p>
            </div>
            <div className="animated">
              <img
                src={siteData.images.about}
                alt="Kevin coach sportif"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-8 text-center">
              Ma philosophie
            </h2>
            <div className="network-card">
              <p className="text-xl text-[var(--text-secondary)] leading-relaxed text-center">
                {siteData.coachBio.philosophy}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Formations & Expertise
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {siteData.coachBio.qualities.map((quality, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 network-card animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                  <Award className="text-white" size={24} />
                </div>
                <p className="text-lg text-[var(--text-secondary)] pt-2">{quality}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Mes valeurs
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-2xl mx-auto">
              Ce qui guide mon accompagnement au quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {siteData.values.map((value, index) => {
              const iconMap = {
                ear: Heart,
                heart: Heart,
                award: Award,
                'trending-up': TrendingUp
              };
              const Icon = iconMap[value.icon] || Target;
              
              return (
                <div
                  key={index}
                  className="text-center animated"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[var(--text-secondary)]">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-[var(--bg-subtle)]">
        <div className="container-max">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: "5+ ans",
                label: "D'expérience en coaching"
              },
              {
                number: "5.0/5",
                label: "Note moyenne client"
              },
              {
                number: "100%",
                label: "De clients satisfaits"
              }
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center network-card animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-5xl md:text-6xl font-bold text-[var(--brand-primary)] mb-3">
                  {stat.number}
                </div>
                <p className="text-xl text-[var(--text-secondary)]">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              En action
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              siteData.images.consultation,
              siteData.images.professional,
              siteData.images.coaching2
            ].map((image, index) => (
              <div
                key={index}
                className="animated overflow-hidden rounded-3xl shadow-lg"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <img
                  src={image}
                  alt={`Coaching ${index + 1}`}
                  className="w-full h-[300px] object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Travaillons ensemble
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Prêt à créer ton déclic ? Contacte-moi pour une première séance
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                Prendre rendez-vous
              </Link>
              <a
                href={`tel:${siteData.business.phone.replace(/\s/g, '')}`}
                className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[var(--brand-dark)]"
              >
                {siteData.business.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default APropos;
